import { buildAppPath } from './tenant';

/**
 * Role normalization utilities to ensure consistent role handling across the frontend
 * Matches backend role_utils.py normalization logic
 */

export const ROLES = {
  CLIENT_ADMIN: 'client_admin',
  BRANCH_ADMIN: 'branch_admin',
  UNDERWRITER: 'underwriter',
  LOAN_OFFICER: 'loan_officer',
  LOAN_PROCESSOR: 'loan_processor',
  CLOSER: 'closer',
  AUDITOR: 'auditor',
  READ_ONLY: 'read_only',
  CONSUMER: 'consumer',
};

const ROLE_METADATA = {
  [ROLES.CLIENT_ADMIN]: {
    label: 'Client Admin',
    description: 'Full control of the tenant: manage employees, branches, and reporting.',
    requiresNmls: false,
  },
  [ROLES.BRANCH_ADMIN]: {
    label: 'Branch Admin',
    description: 'Oversees one or more branches. Can invite employees and manage pipeline activity.',
    requiresNmls: true,
  },
  [ROLES.UNDERWRITER]: {
    label: 'Underwriter',
    description: 'Renders the credit decision, and clears or declines conditions. Not an MLO, so no NMLS licence is required.',
    requiresNmls: false,
  },
  [ROLES.LOAN_OFFICER]: {
    label: 'Loan Officer',
    description: 'Originates new loans and collaborates with processors. Active NMLS is required.',
    requiresNmls: true,
  },
  [ROLES.LOAN_PROCESSOR]: {
    label: 'Loan Processor',
    description: 'Shepherds files through underwriting and closing. NMLS is required for compliance checks.',
    requiresNmls: true,
  },
  [ROLES.CLOSER]: {
    label: 'Closer',
    description: 'Prepares and issues the closing package.',
    requiresNmls: false,
  },
  [ROLES.AUDITOR]: {
    label: 'Auditor',
    description: 'Reviews decided files for compliance and reads the audit trail. Cannot originate or decide.',
    requiresNmls: false,
  },
  [ROLES.READ_ONLY]: {
    label: 'Read Only',
    description: 'Can view dashboards and pipeline data without editing privileges.',
    requiresNmls: false,
  },
  [ROLES.CONSUMER]: {
    label: 'Consumer',
    description: 'Borrower-facing experience with limited administrative access.',
    requiresNmls: false,
  },
};

// Role aliases mapping to canonical roles
const ROLE_ALIASES = {
  // Primary mappings
  'client_admin': ROLES.CLIENT_ADMIN,
  'branch_admin': ROLES.BRANCH_ADMIN,
  'underwriter': ROLES.UNDERWRITER,
  'loan_officer': ROLES.LOAN_OFFICER,
  'loan_processor': ROLES.LOAN_PROCESSOR,
  'closer': ROLES.CLOSER,
  'auditor': ROLES.AUDITOR,
  'read_only': ROLES.READ_ONLY,
  'consumer': ROLES.CONSUMER,

  // Stytch member variations
  'stytch_admin': ROLES.CLIENT_ADMIN,
  'stytch_member': ROLES.READ_ONLY,

  // Legacy variations
  'organization': ROLES.CLIENT_ADMIN,
  'org_admin': ROLES.CLIENT_ADMIN,
  'organization_admin': ROLES.CLIENT_ADMIN,
  'company_admin': ROLES.CLIENT_ADMIN,
  'admin': ROLES.CLIENT_ADMIN,
  'platform_admin': ROLES.CLIENT_ADMIN,
  'platform_super_admin': ROLES.CLIENT_ADMIN,
  'superadmin': ROLES.CLIENT_ADMIN,

  // Loan officer variations. `loanofficer` closes a round-trip hole: the legacy
  // value this file itself produces in `mapRoleToAppRole` is `loanOfficer`, and
  // feeding that back in matched nothing and fail-closed to `read_only`. Any
  // caller reading the mapped value out of the store and normalising it — the
  // employee screen does exactly that — saw a loan officer as read-only.
  'lo': ROLES.LOAN_OFFICER,
  'loanofficer': ROLES.LOAN_OFFICER,
  'loan officer': ROLES.LOAN_OFFICER,
  'loan_officer_admin': ROLES.LOAN_OFFICER,
  'mortgage_officer': ROLES.LOAN_OFFICER,

  // Loan processor variations. `loanprocessor` for the same reason as above.
  'lp': ROLES.LOAN_PROCESSOR,
  'loanprocessor': ROLES.LOAN_PROCESSOR,
  'processor': ROLES.LOAN_PROCESSOR,
  'loan processor': ROLES.LOAN_PROCESSOR,
  'mortgage_processor': ROLES.LOAN_PROCESSOR,

  // Branch variations
  'branch manager': ROLES.BRANCH_ADMIN,
  'branch_manager': ROLES.BRANCH_ADMIN,
  'branchadmin': ROLES.BRANCH_ADMIN,

  // Underwriter variations
  'under_writer': ROLES.UNDERWRITER,
  'underwriting': ROLES.UNDERWRITER,

  // Auditor variations. `qa` and `qc` were the working names for this role
  // before it was called auditor, so they resolve rather than fail closed.
  'qa': ROLES.AUDITOR,
  'qc': ROLES.AUDITOR,
  'quality_assurance': ROLES.AUDITOR,

  // Read-only variations
  'viewer': ROLES.READ_ONLY,
  'readonly': ROLES.READ_ONLY,
  'read only': ROLES.READ_ONLY,
  'user': ROLES.READ_ONLY
};

/**
 * Normalize a role string to its canonical form
 * @param {string} role - The role to normalize (case-insensitive)
 * @returns {string} The normalized role or 'read_only' as default
 */
export function normalizeRole(role) {
  if (!role) {
    return ROLES.READ_ONLY;
  }

  // Convert to lowercase and trim
  const normalizedInput = String(role).toLowerCase().trim();

  // Check against our aliases
  const canonicalRole = ROLE_ALIASES[normalizedInput];

  if (!canonicalRole) {
    if (import.meta.env.DEV) {
      console.warn(`[normalizeRole] Unknown role '${role}', defaulting to read_only`);
    }
    return ROLES.READ_ONLY;
  }

  return canonicalRole;
}

export function getRoleMetadata(role) {
  const normalized = normalizeRole(role);
  return ROLE_METADATA[normalized] || {
    label: normalized,
    description: '',
    requiresNmls: false,
  };
}

export function getRoleDescription(role) {
  return getRoleMetadata(role).description;
}

export function roleRequiresNmls(role) {
  return getRoleMetadata(role).requiresNmls;
}

/**
 * Check if a role has admin privileges
 * @param {string} role - The role to check
 * @returns {boolean} True if the role has admin privileges
 */
export function isAdminRole(role) {
  const normalized = normalizeRole(role);
  return normalized === ROLES.CLIENT_ADMIN || normalized === ROLES.BRANCH_ADMIN;
}

/**
 * Check if a role is scoped to the organization rather than a branch.
 * Mirrors `org_scoped_roles` in user_invite_workflows.py — the backend drops
 * branch_id for these, so the UI must not offer a branch for them either.
 * @param {string} role - The role to check
 * @returns {boolean} True if the role is organization-scoped
 */
export function isOrgScopedRole(role) {
  const normalized = normalizeRole(role);
  return (
    normalized === ROLES.CLIENT_ADMIN ||
    normalized === 'platform_admin' ||
    normalized === 'platform_super_admin'
  );
}

/**
 * Check if a role is an employee role
 * @param {string} role - The role to check
 * @returns {boolean} True if the role is an employee role
 */
export function isEmployeeRole(role) {
  const normalized = normalizeRole(role);
  return normalized === ROLES.LOAN_OFFICER || normalized === ROLES.LOAN_PROCESSOR;
}

/**
 * Get the appropriate landing page for a role
 * @param {string} role - The user's role
 * @returns {string} The landing path for that role
 */
export function landingPathForRole(role) {
  const rawInput = String(role || '').toLowerCase();

  if (rawInput.includes('platform')) {
    return buildAppPath('/platform/dashboard');
  }

  /* Everyone else lands on their dashboard.
   *
   * The loan processor used to land on the pipeline instead, carried over from
   * the fork. It made sense while the dashboard was one generic page built for
   * loan officers: a processor signing in to a screen of leads and lender
   * spread learned nothing, and the pipeline at least held their files. Now
   * that the processor has a dashboard about their own queue, the exception
   * costs them the view that was built for them. */
  return buildAppPath('/dashboard');
}

/**
 * Map raw backend roles to legacy frontend role labels used in Redux/UI
 * @param {string} role
 * @returns {string}
 */
export function mapRoleToAppRole(role) {
  const raw = String(role || '').toLowerCase();

  if (raw.includes('platform')) {
    return 'superAdmin';
  }

  switch (normalizeRole(role)) {
    case ROLES.CLIENT_ADMIN:
      return 'organization';
    case ROLES.BRANCH_ADMIN:
      return 'branchAdmin';
    case ROLES.UNDERWRITER:
      return 'underwriter';
    case ROLES.LOAN_OFFICER:
      return 'loanOfficer';
    case ROLES.LOAN_PROCESSOR:
      return 'loanProcessor';
    case ROLES.CLOSER:
      return 'closer';
    case ROLES.AUDITOR:
      return 'auditor';
    case ROLES.CONSUMER:
      return 'consumer';
    /* **`read_only` and the fallback are no longer `consumer`.** They were,
       and it was the single most damaging line in this file: `consumer` is a
       real value with real meaning, so an unrecognised *staff* role did not
       merely fail to route, it was actively treated as a borrower. It fetched
       notifications from the B2C endpoint, resolved the assistant to the b2c
       portal, called the consumer amortisation query, took the consumer
       onboarding branch at login, rendered the borrower sidebar, and printed
       "consumer" in the invitations table.

       `readOnly` is its own legacy value now. It has no case in
       `AppContentNew`'s route switch either, so a read-only user still cannot
       route — but they land on the login screen rather than inside the
       borrower product, which is the correct failure. Fixing that properly is
       a separate change; this one stops the mislabelling. */
    case ROLES.READ_ONLY:
    default:
      return 'readOnly';
  }
}

/**
 * Get display name for a role
 * @param {string} role - The role to get display name for
 * @returns {string} Human-friendly display name
 */
export function getRoleDisplayName(role) {
  const metadata = getRoleMetadata(role);
  return metadata.label || 'Read Only';
}

/**
 * Get role hierarchy level (higher number = more privileges)
 * @param {string} role - The role to check
 * @returns {number} Hierarchy level (0-4)
 */
export function getRoleHierarchy(role) {
  const normalized = normalizeRole(role);

  /* Renumbered to make room, with every existing relative order preserved:
     client admin over branch admin over loan officer over processor over
     read-only over consumer. Only the gaps changed.

     Two things worth knowing before relying on this. Nothing calls
     `getRoleHierarchy` or `canManageRole` today, so these numbers are inert
     and the ordering below is a statement of intent rather than behaviour.
     And this scale puts the loan officer *above* the processor while the
     backend's `ROLE_HIERARCHY` has the processor above the officer (50 vs 40),
     so the two have never agreed. Left alone rather than "fixed", because
     flipping it would change `canManageRole` for existing roles the moment
     something starts calling it. The three new roles are ordered among
     themselves as the backend orders them. */
  const hierarchy = {
    [ROLES.CLIENT_ADMIN]: 7,
    [ROLES.BRANCH_ADMIN]: 6,
    [ROLES.UNDERWRITER]: 5,
    [ROLES.LOAN_OFFICER]: 4,
    [ROLES.LOAN_PROCESSOR]: 3,
    [ROLES.CLOSER]: 2,
    [ROLES.AUDITOR]: 1,
    [ROLES.READ_ONLY]: 0,
    [ROLES.CONSUMER]: -1
  };

  return hierarchy[normalized] || 0;
}

/**
 * Check if a user role has permission to perform actions on target role
 * @param {string} userRole - The role of the user performing the action
 * @param {string} targetRole - The role being acted upon
 * @returns {boolean} True if user has permission
 */
export function canManageRole(userRole, targetRole) {
  return getRoleHierarchy(userRole) > getRoleHierarchy(targetRole);
}
